/** @jsx jsx */

import React, {
  Fragment as _,
  memo,
  useMemo,
  useState,
  useContext,
  useEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import EditableText from 'components/lib/EditableText';
import { colors } from 'styles/variables';
import renderBlock from 'services/apiHelpers/renderBlock';
import { Row, Column, Button, Box } from '@chameleoncreator/creator-ui';
import Image from 'components/lib/Image';
import Video from 'components/lib/Video';
import SVG from 'components/lib/SVG';
import ContinueBtn from 'components/lib/ContinueBtn/continueBtn_v2';
import Container from 'components/Layouts/ContainerLogical';
import { interactionContext } from './interactionContext';
import Media from 'components/lib/Media';
import { Block } from '@chameleoncreator/creator-app';
import getBlockById from 'services/apiHelpers/getBlockById';
import IELayout from 'components/Layouts/IELayout';
import EditableButton from 'components/lib/EditableBtn';
import AssessmentQuestion from './AssessmentQuestion';
import AssessmentQuestionOption from './AssessmentQuestionOption';
import AssessmentQuestionOptions from './AssessmentQuestionOptions';
import Audio from 'components/lib/Audio';
import PDF from 'components/lib/PDF';
import Embed from 'components/lib/Embed';
import findByKeyVal, { findByKeyVal_V2 } from 'services/findByKeyVal';
import setBlockProps from 'services/apiHelpers/setBlockProps';

const { lPink, lGrey0, lGrey1, lGrey2 } = colors;

const SingleColumnLayout = Row.bind({}) as any;
SingleColumnLayout.defaultProps = {
  ...(Row as any).defaultProps,
  flexDirection: 'column',
};

const TwoColumnLayout = Row.bind({}) as any;
TwoColumnLayout.defaultProps = {
  ...(Row as any).defaultProps,
  flexDirection: 'row',
};

const Components = {
  Row,
  Column,
  Text: EditableText,
  QuestionCounter: ({ children, ...props }) => {
    //
    return children;
  },
  AssessmentQuestion,
  AssessmentQuestionOptions,
  AssessmentQuestionOption,
  Image,
  TwoColumnLayout,
  SingleColumnLayout,
  ContinueButton: ContinueBtn,
  Video,
  SVG,
  Media,
  Audio: null,
  EditableButton,
  Embed: null,
  Transcript: null,
  LearnMore: () => null,
  PDF,
  OpenPDF: ({ children, ...props }) => children,
  ...IELayout(interactionContext),
};

type InteractionContextValue = {
  id: string;
  props: { [key: string]: any };
};

const AssessmentQuestionDisplay = memo((props: any) => {
  const interactionBlock: Block = getBlockById(props.blockId);

  let correctOptions = interactionBlock?.props?.correctOptions || [0];
  if (!correctOptions.length || correctOptions.includes(undefined)) {
    correctOptions = [0];
  }

  // console.log('SAVED CORRECT OPTIONS :', correctOptions);

  const assessmentQuestionOptions = findByKeyVal_V2(
    interactionBlock.id,
    'type',
    'AssessmentQuestionOptions'
  ) as Block;

  const assessmentQuestionOptionsChildren = assessmentQuestionOptions.children as Block[];
  const optionsIds = assessmentQuestionOptionsChildren.map(entry => {
    return entry.id;
  });

  correctOptions = correctOptions.map(entry => {
    return typeof entry === 'string' ? entry : optionsIds[entry];
  });

  // console.log('TRANSFORMED CORRECT OPTIONS :', correctOptions);

  useEffect(() => {
    // if any of the saved correctOptions are ' old ' / id based
    // transform them immediately to indicies and save

    const correctOptionsHaveSavedIds = correctOptions.some(entry => {
      return typeof entry === 'string';
    });

    if (!correctOptionsHaveSavedIds) {
      return;
    }

    const updatedCorrectOptions = correctOptions.map(entry => {
      return typeof entry === 'string'
        ? assessmentQuestionOptionsChildren.findIndex(child => {
            return child.id === entry;
          })
        : entry;
    });

    setBlockProps(interactionBlock, {
      correctOptions: updatedCorrectOptions,
    });
  }, [correctOptions.join()]);

  const interactionContextValue: InteractionContextValue = {
    id: interactionBlock.id,
    props: {
      ...interactionBlock.props,
      correctOptions,
    },
  };

  return (
    <Container
      id={interactionBlock.id}
      key={interactionBlock.id}
      {...interactionBlock.props}
      {...props}
    >
      <interactionContext.Provider value={interactionContextValue}>
        {(interactionBlock.children as Block[]).map(child => {
          return renderBlock(child, Components);
        })}
      </interactionContext.Provider>
    </Container>
  );
});
// shouldUpdate

export default AssessmentQuestionDisplay;
